/**
 * Profile Context Provider for managing profile state across the app
 */

import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { Profile, ProfileContextType, PilotInfo } from '../types/profile';
import { ProfileService } from '../services/profileService';
import * as storageRouterService from '../services/storageRouterService';
import { googleDriveRepository } from '../services/repository/GoogleDriveRepository';
import { googleDriveService } from '../services/googleDriveService';
import { insuranceService } from '../services/insuranceService';
import { healthService } from '../services/healthService';
import { financialService } from '../services/financialService';
import { usesGoogleDrive } from '../constants/planLimits';
import { syncCountsFromGoogleDriveMeta, cacheItemCounts } from '../components/ui/LoadingState';
import { isValidAvatarUrl } from '../utils/avatar';
import { adminMetricsService } from '../services/adminMetricsService';
import { firebaseAuthService } from '../services/firebaseAuthService';
import { showToast } from '../components/ui/Toast';

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

interface ProfileProviderProps {
  children: ReactNode;
}

export function ProfileProvider({ children }: ProfileProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [currentProfile, setCurrentProfile] = useState<Profile | null>(null);
  const [activeProfile, setActiveProfile] = useState<Profile | null>(null);
  const [pilotInfo, setPilotInfo] = useState<PilotInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true); // True while loading profile/data (including Google Drive)
  const [isDriveDataLoading, setIsDriveDataLoading] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [authUser, setAuthUser] = useState<User | null>(null);

  // Keep latest profiles in a ref for synchronous access in async callbacks
  const profilesRef = useRef(profiles);
  profilesRef.current = profiles;

  // Track pending optimistic updates to prevent Firestore listener from overwriting them
  const pendingUpdatesRef = useRef<Map<string, { timestamp: number; updates: any }>>(new Map());

  const googleDriveDataRef = useRef<{
    isProUser: boolean;
    addresses: Profile['addresses'];
    vehicles: Profile['vehicles'];
    insurancePolicies: Profile['insurancePolicies'];
  } | null>(null);

  // CRITICAL: Track the unsubscribe function for profile subscriptions
  // This prevents multiple subscriptions from being created and causing Firestore errors
  const unsubscribeProfilesRef = useRef<(() => void) | null>(null);
  const unsubscribeFamilyDocRef = useRef<(() => void) | null>(null);

  // Prevent duplicate initialization in React StrictMode/dev re-renders
  const initInProgressRef = useRef(false);

  // Listen for Firebase auth state changes
  useEffect(() => {
    let isInitialCallback = true;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthUser(user);
      setAuthReady(true);

      // The first callback reflects the resolved persistence state, not an
      // actual sign-out event. Skip stale-session detection here — the second
      // useEffect handles the genuine no-session case after authReady is set.
      if (isInitialCallback) {
        isInitialCallback = false;
        return;
      }

      // If Firebase auth is gone but localStorage thinks we're still authenticated,
      // this is a stale session (token expired, revoked, or tab resumed after long inactivity).
      // Clean up immediately to prevent broken intermediate state.
      if (!user && localStorage.getItem('isAuthenticated') === 'true') {
        const userType = localStorage.getItem('lucidDock_userType');
        // Only auto-clear for pilot users (family members don't use Firebase Auth directly)
        // Skip redirect for public pages (emergency access, share bundles, invites)
        const isPublicPage = window.location.pathname.startsWith('/share')
          || window.location.pathname.startsWith('/invite');
        if (userType === 'pilot') {
          firebaseAuthService.clearSessionData();
          // Reset loading/profile state to prevent broken UI during redirect
          setIsLoading(false);
          setCurrentProfile(null);
          setProfiles([]);
          if (!isPublicPage) {
            navigate('/', { replace: true });
          }
        }
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Initialize profiles when auth is ready
  useEffect(() => {
    // Wait for auth state to be resolved before touching Firestore
    if (!authReady) {
      return;
    }

    // Prevent duplicate init (React StrictMode can mount/unmount twice in dev)
    if (initInProgressRef.current) {
      return;
    }
    initInProgressRef.current = true;

    // Flag to track if component is still mounted
    let isMounted = true;

    // Async function to initialize auth and set up listeners
    const initializeAuth = async () => {
      try {
        const firebaseUser = authUser;
        if (!isMounted) return;

        // Now process the auth state (same logic as before, but inline)
        // Check current path inside callback to handle navigation correctly
        // Skip all profile loading on landing/invite/share/OAuth pages to avoid permission errors
        const currentPath = window.location.pathname;
        const isPublicPage = currentPath === '/' || currentPath.startsWith('/invite/') || currentPath.startsWith('/share/') || currentPath.startsWith('/oauth/') || currentPath === '/privacy' || currentPath === '/terms';

        if (isPublicPage) {
          return;
        }

        // Get user ID from localStorage (session data)
        const userId = ProfileService.getCurrentUserId();
        const userType = localStorage.getItem('lucidDock_userType');

        // If no userId in localStorage, redirect to auth
        if (!userId) {
          navigate('/');
          return;
        }

        // If user is not authenticated (e.g., exited private guest mode), clear session and redirect
        const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
        if (!isAuthenticated) {
          firebaseAuthService.clearSessionData();
          navigate('/');
          return;
        }

        // For Pilots: If we have a userId but Firebase Auth is not authenticated,
        // the session is stale - clear it and redirect to auth
        // Family members don't use Firebase Auth directly, so skip this check for them
        if (userType === 'pilot' && !firebaseUser) {
          firebaseAuthService.clearSessionData();
          navigate('/');
          return;
        }

        // For Pilots: Force refresh the auth token to ensure Firestore has the latest auth state
        // This prevents "permission denied" errors due to stale tokens after sign-in
        if (userType === 'pilot' && firebaseUser) {
          try {
            await firebaseUser.getIdToken(true);
          } catch (tokenError) {
            console.warn('ProfileContext: Failed to refresh auth token, continuing anyway:', tokenError);
          }
        }

        // Initialize profiles from Firestore
        const loadedProfiles = await ProfileService.initializeProfiles(userId);

        // Sync profile picture and name from localStorage if they exist
        // CRITICAL FIX: Use Firebase Auth photoURL as fallback when localStorage is empty
        // This ensures profile pictures display even if localStorage wasn't set during sign-in
        let profilePictureUrl = localStorage.getItem('profilePictureUrl');
        if (!profilePictureUrl && firebaseUser?.photoURL) {
          profilePictureUrl = firebaseUser.photoURL;
          // Persist to localStorage for future use
          localStorage.setItem('profilePictureUrl', firebaseUser.photoURL);
        }
        const firstName = localStorage.getItem('firstName');
        const lastName = localStorage.getItem('lastName');

        if (loadedProfiles.length > 0) {
          const currentProfileId = ProfileService.getCurrentProfileId();
          const userType = localStorage.getItem('lucidDock_userType');

          // For Pilots: Always select their OWN profile (where they are the owner)
          // This ensures that when a user who is both a Pilot AND a Co-Pilot on another profile
          // signs in as a Pilot, they see their own profile, not someone else's
          let currentProfile: Profile | undefined;

          if (userType === 'pilot') {
            // Find the profile matching the current account type (business or personal)
            const accountType = localStorage.getItem('lucidDock_accountType') || 'personal';
            currentProfile = loadedProfiles.find(p => p.ownerId === userId && p.type === accountType);

            // If no matching profile, try to create one
            if (!currentProfile) {
              const ownedProfile = loadedProfiles.find(p => p.ownerId === userId);
              if (ownedProfile && accountType !== ownedProfile.type) {
                // User has a profile but for a different type — create one for the current type
                try {
                  const name = [firstName, lastName].filter(Boolean).join(' ') || 'My Profile';
                  const newProfile = await ProfileService.createProfile(name, userId, accountType as 'personal' | 'business');
                  loadedProfiles.push(newProfile);
                  currentProfile = newProfile;
                } catch {
                  // Fallback to existing profile
                  currentProfile = ownedProfile;
                }
              } else {
                // Fallback to any owned profile or stored ID
                currentProfile = ownedProfile || loadedProfiles.find(p => p.id === currentProfileId) || loadedProfiles[0];
              }
            }

            if (currentProfile) {
              ProfileService.setCurrentProfileId(currentProfile.id);
            }
          } else {
            // For family members (Co-Pilot/Passenger): ALWAYS prefer the shared profile from Pilot
            // This handles cases where the user is BOTH a Pilot on their own profile AND
            // a Co-Pilot/Passenger on another profile - when logged in as 'family', show shared profile

            // First, try to find a shared profile (where user is NOT the owner)
            const sharedProfile = loadedProfiles.find(p => p.ownerId !== userId);

            if (sharedProfile) {
              // Use the shared profile (Pilot's profile where this user is Co-Pilot/Passenger)
              currentProfile = sharedProfile;
            } else if (currentProfileId) {
              // Fallback to stored currentProfileId if no shared profile found
              currentProfile = loadedProfiles.find(p => p.id === currentProfileId) || loadedProfiles[0];
            } else {
              currentProfile = loadedProfiles[0];
            }

            // Update currentProfileId to point to the correct profile
            if (currentProfile) {
              ProfileService.setCurrentProfileId(currentProfile.id);
            }
          }

          if (currentProfile) {
            const updates: Partial<typeof currentProfile> = {};
            let needsUpdate = false;

            // Clean up corrupted avatar data (avatar set to display name instead of URL)
            if (currentProfile.avatar && !isValidAvatarUrl(currentProfile.avatar)) {
              updates.avatar = ''; // Clear invalid avatar
              needsUpdate = true;
            }

            // Clean up corrupted member avatars
            const membersWithCorruptedAvatars = currentProfile.members?.some(
              m => m.avatar && !isValidAvatarUrl(m.avatar)
            );
            if (membersWithCorruptedAvatars) {
              updates.members = currentProfile.members?.map(m => {
                if (m.avatar && !isValidAvatarUrl(m.avatar)) {
                  return {
                    ...m,
                    avatar: '',
                    avatarType: 'initials' as const
                  };
                }
                return m;
              });
              needsUpdate = true;
            }

            // Update avatar if available and different
            // BUT only if user doesn't have a custom-uploaded photo
            // Custom photos can be: Firebase Storage URLs or base64 data URLs
            // Check BOTH profile.avatar AND owner member's avatar (belt and suspenders)
            const ownerMemberForAvatar = currentProfile.members?.find(m => m.role === 'owner' && m.userId === userId);

            // Helper to check if an avatar URL is a custom upload (not a Google photo)
            const isCustomUploadedPhoto = (avatar: string | undefined | null): boolean => {
              if (!avatar) return false;
              return avatar.includes('firebasestorage') || avatar.startsWith('data:image/');
            };

            // Check if profile.avatar or owner member.avatar has a custom photo
            const hasCustomPhotoOnProfile = isCustomUploadedPhoto(currentProfile.avatar);
            const hasCustomPhotoOnMember = isCustomUploadedPhoto(ownerMemberForAvatar?.avatar);
            const hasCustomPhoto = hasCustomPhotoOnProfile || hasCustomPhotoOnMember;

            // CRITICAL FIX: Check BOTH profile.avatar AND owner member.avatar need updating
            // Previously, if profile.avatar matched but member.avatar was empty, no sync happened
            const profileAvatarNeedsUpdate = currentProfile.avatar !== profilePictureUrl;
            const memberAvatarNeedsUpdate = ownerMemberForAvatar && ownerMemberForAvatar.avatar !== profilePictureUrl;

            if (profilePictureUrl && !hasCustomPhoto && (profileAvatarNeedsUpdate || memberAvatarNeedsUpdate)) {
              if (profileAvatarNeedsUpdate) {
                updates.avatar = profilePictureUrl;
              }
              needsUpdate = true;

              // CRITICAL FIX: Use updates.members if it exists (from corrupted avatar cleanup)
              // to avoid overwriting those changes. Previously this always used currentProfile.members
              // which would lose any corrupted avatar fixes for non-owner members.
              const membersBase = updates.members || currentProfile.members || [];
              updates.members = membersBase.map(m => {
                if (m.role === 'owner' && m.userId === userId) {
                  return {
                    ...m,
                    avatar: profilePictureUrl,
                    avatarType: 'photo' as const
                  };
                }
                return m;
              });
            }

            // Update profile name if it's still "My Profile" and we have firstName/lastName
            if (currentProfile.name === 'My Profile' && (firstName || lastName)) {
              const fullName = `${firstName || ''} ${lastName || ''}`.trim();
              if (fullName) {
                updates.name = fullName;
                needsUpdate = true;

                // Also update displayName for owner member
                if (!updates.members) {
                  updates.members = currentProfile.members?.map(m => {
                    if (m.role === 'owner' && m.userId === userId && m.displayName === 'Me') {
                      return {
                        ...m,
                        displayName: fullName
                      };
                    }
                    return m;
                  });
                } else {
                  updates.members = updates.members.map(m => {
                    if (m.role === 'owner' && m.userId === userId && m.displayName === 'Me') {
                      return {
                        ...m,
                        displayName: fullName
                      };
                    }
                    return m;
                  });
                }
              }
            }

            // Sync owner member's displayName with profile name if they don't match
            const ownerMember = currentProfile.members?.find(m => m.role === 'owner' && m.userId === userId);
            if (ownerMember && currentProfile.name !== 'My Profile' && ownerMember.displayName !== currentProfile.name) {
              if (!updates.members) {
                updates.members = currentProfile.members?.map(m => {
                  if (m.role === 'owner' && m.userId === userId) {
                    return {
                      ...m,
                      displayName: currentProfile.name
                    };
                  }
                  return m;
                });
              } else {
                updates.members = updates.members.map(m => {
                  if (m.role === 'owner' && m.userId === userId) {
                    return {
                      ...m,
                      displayName: currentProfile.name
                    };
                  }
                  return m;
                });
              }
              needsUpdate = true;
            }

            // Sync owner member's email if missing (for existing profiles)
            const userEmail = localStorage.getItem('userEmail');
            const currentOwnerMember = updates.members?.find(m => m.role === 'owner' && m.userId === userId) ||
              currentProfile.members?.find(m => m.role === 'owner' && m.userId === userId);
            if (userEmail && currentOwnerMember && !currentOwnerMember.email) {
              if (!updates.members) {
                updates.members = currentProfile.members?.map(m => {
                  if (m.role === 'owner' && m.userId === userId) {
                    return {
                      ...m,
                      email: userEmail
                    };
                  }
                  return m;
                });
              } else {
                updates.members = updates.members.map(m => {
                  if (m.role === 'owner' && m.userId === userId && !m.email) {
                    return {
                      ...m,
                      email: userEmail
                    };
                  }
                  return m;
                });
              }
              needsUpdate = true;
            }

            // Apply updates if needed
            if (needsUpdate) {
              await ProfileService.updateProfile(currentProfile.id, updates);

              // Apply updates to the loaded profile immediately to avoid race condition
              Object.assign(currentProfile, updates);
            }
          }
        }

        // Use the same profile selection logic as above
        // currentProfileId should already be correct (set above for Pilots to their own profile)
        const finalProfileId = ProfileService.getCurrentProfileId();
        let current = loadedProfiles.find(p => p.id === finalProfileId) || loadedProfiles[0];

        // Store profile owner ID for encryption key derivation
        if (current?.ownerId) {
          ProfileService.setProfileOwnerId(current.ownerId);
        }

        // Sync family member's Google profile (full name + avatar) to Firestore member record
        // This ensures Co-Pilots/Passengers show their full Google name and photo, not just
        // the first name the Pilot typed during invitation
        if (userType === 'family' && current && auth.currentUser) {
          const userEmail = localStorage.getItem('userEmail');
          const familyMember = current.members?.find(
            m => m.userId === userId || (userEmail && m.email === userEmail)
          );
          if (familyMember) {
            const googleDisplayName = auth.currentUser.displayName;
            const googlePhotoURL = auth.currentUser.photoURL;
            const needsNameSync = googleDisplayName && familyMember.displayName !== googleDisplayName;
            const needsAvatarSync = googlePhotoURL && familyMember.avatar !== googlePhotoURL;

            if (needsNameSync || needsAvatarSync) {
              const updatedMembers = (current.members || []).map(m => {
                if (m.userId === familyMember.userId) {
                  return {
                    ...m,
                    ...(needsNameSync ? { displayName: googleDisplayName } : {}),
                    ...(needsAvatarSync ? { avatar: googlePhotoURL, avatarType: 'photo' as const } : {}),
                  };
                }
                return m;
              });
              ProfileService.updateProfile(current.id, { members: updatedMembers }).catch(err => console.warn('Profile sync failed:', err));
              current = { ...current, members: updatedMembers };
              // Update in loadedProfiles array too
              const idx = loadedProfiles.findIndex(p => p.id === current.id);
              if (idx >= 0) loadedProfiles[idx] = current;
            }
          }
        }

        // Fetch pilot info first to check if user is Pro (uses Google Drive)
        let pilotData: PilotInfo | null = null;
        if (current?.ownerId) {
          pilotData = await ProfileService.getPilotInfo(current.ownerId);

          // Fallback for Co-Pilots: getPilotInfo() only works when the current user IS the pilot
          // (Firestore rules block cross-user reads). For family members, construct pilotInfo
          // from the profile document which they already have access to.
          if (!pilotData && userType === 'family' && current.membershipPlan) {
            const ownerMember = current.members?.find(m => m.role === 'owner');
            pilotData = {
              firstName: ownerMember?.displayName || '',
              fullName: ownerMember?.displayName || 'Owner',
              email: ownerMember?.email,
              photoURL: ownerMember?.avatar,
              membershipPlan: current.membershipPlan as PilotInfo['membershipPlan'],
            };
          }

          if (pilotData) {
            setPilotInfo(pilotData);
            if (pilotData.accountType) {
              localStorage.setItem('lucidDock_accountType', pilotData.accountType);
            }
            if (pilotData.membershipPlan) {
              localStorage.setItem('lucidDock_membershipPlan', pilotData.membershipPlan);

              // Sync plan to profile document so Co-Pilots can read it (they can't access pilot doc)
              if (current.membershipPlan !== pilotData.membershipPlan) {
                ProfileService.updateProfile(current.id, { membershipPlan: pilotData.membershipPlan }).catch(err => console.warn('Profile sync failed:', err));
                current = { ...current, membershipPlan: pilotData.membershipPlan };
              }
            }

            // Sync permanent profile picture from pilot document to localStorage + profile
            // The pilot document stores the Firebase Storage URL (permanent), while
            // localStorage may have an expired Google URL or be empty.
            const permanentPhotoURL = pilotData.photoURL;
            if (permanentPhotoURL && permanentPhotoURL.includes('firebasestorage')) {
              const storedUrl = localStorage.getItem('profilePictureUrl');
              if (storedUrl !== permanentPhotoURL) {
                localStorage.setItem('profilePictureUrl', permanentPhotoURL);
              }
              // Also update the profile avatar + owner member avatar if stale
              if (current) {
                const profileAvatar = current.avatar;
                const ownerMember = current.members?.find(m => m.role === 'owner' && m.userId === userId);
                if (profileAvatar !== permanentPhotoURL || (ownerMember && ownerMember.avatar !== permanentPhotoURL)) {
                  const memberUpdates = (current.members || []).map(m =>
                    m.role === 'owner' && m.userId === userId
                      ? { ...m, avatar: permanentPhotoURL, avatarType: 'photo' as const }
                      : m
                  );
                  await ProfileService.updateProfile(current.id, {
                    avatar: permanentPhotoURL,
                    members: memberUpdates,
                  });
                  current.avatar = permanentPhotoURL;
                  current.members = memberUpdates;
                }
              }
            }

            // Cache admin status early so Sidebar can show Dashboard/Settings immediately
            // Use synchronous check for primary admin (instant), async for additional admins
            if (pilotData.email) {
              // Synchronous check for primary admin - no Firestore call needed
              if (adminMetricsService.isPrimaryAdmin(pilotData.email)) {
                localStorage.setItem('lucidDock_isAdmin', 'true');
              } else {
                // Async check for additional admins (timeout after 5s)
                Promise.race([
                  adminMetricsService.isAuthorizedAdminAsync(pilotData.email),
                  new Promise<boolean>(resolve => setTimeout(() => resolve(false), 5000)),
                ]).then(isAdmin => {
                  localStorage.setItem('lucidDock_isAdmin', String(isAdmin));
                }).catch(() => {
                  // Ignore errors - Sidebar will check again
                });
              }
            }
          }
        }

        // For Pro users who chose Google Drive AND have it connected: Load data from Drive
        const storagePreference = localStorage.getItem('lucidDock_storageProvider');
        const userChoseDrive = storagePreference !== 'firebase' && googleDriveService.isConnected();
        if (pilotData?.membershipPlan && usesGoogleDrive(pilotData.membershipPlan) && userChoseDrive) {
          try {
            const isFamilyMember = userType === 'family';

            if (isFamilyMember) {
              if (current.driveRootFolderId && pilotData.email) {
                await googleDriveRepository.initializeAsFamily(
                  current.id,
                  current.name,
                  current.driveRootFolderId,
                  pilotData.email
                );

                localStorage.setItem('lucidDock_familyDriveRootFolderId', current.driveRootFolderId);
                localStorage.setItem('lucidDock_familyDriveOwnerEmail', pilotData.email);
              } else {
                console.warn('[ProfileContext] Family member missing driveRootFolderId or pilotData.email — skipping Drive init');
              }
            } else {
              await googleDriveRepository.initialize(current.id, current.name);

              const rootId = googleDriveService.getRootFolderId();
              if (rootId && !current.driveRootFolderId) {
                ProfileService.updateProfile(current.id, { driveRootFolderId: rootId });
                current = { ...current, driveRootFolderId: rootId };
              }
            }

            if (googleDriveRepository.isReady()) {
              const [driveAddresses, driveVehicles, driveInsurance] = await Promise.all([
                googleDriveRepository.getAddresses(current.id),
                googleDriveRepository.getVehicles(current.id),
                googleDriveRepository.getInsurancePolicies(current.id),
              ]);

              syncCountsFromGoogleDriveMeta({
                addresses: driveAddresses.length,
                vehicles: driveVehicles.length,
                insurancePolicies: driveInsurance.length,
              });

              googleDriveDataRef.current = {
                isProUser: true,
                addresses: driveAddresses,
                vehicles: driveVehicles,
                insurancePolicies: driveInsurance,
              };

              current = {
                ...current,
                addresses: driveAddresses,
                vehicles: driveVehicles,
                insurancePolicies: driveInsurance,
              };

              const profileIndex = loadedProfiles.findIndex(p => p.id === current.id);
              if (profileIndex >= 0) {
                loadedProfiles[profileIndex] = current;
              }
            } else {
              console.warn('ProfileContext: Google Drive repository not ready — clearing entity data for Pro user');
              current = {
                ...current,
                addresses: [],
                vehicles: [],
                insurancePolicies: [],
              };

              const clearedIndex = loadedProfiles.findIndex(p => p.id === current.id);
              if (clearedIndex >= 0) {
                loadedProfiles[clearedIndex] = current;
              }

              const justConnected = sessionStorage.getItem('lucidDock_driveJustConnected');
              const isRecentSignup = justConnected && (Date.now() - parseInt(justConnected, 10)) < 15000;
              if (isRecentSignup) {
                sessionStorage.removeItem('lucidDock_driveJustConnected');
              } else {
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('openStorageSettings'));
                }, 500);
              }
            }
          } catch (error) {
            console.error('ProfileContext: Error loading Google Drive data:', error);
            current = {
              ...current,
              addresses: [],
              vehicles: [],
              insurancePolicies: [],
            };
            const errorIndex = loadedProfiles.findIndex(p => p.id === current.id);
            if (errorIndex >= 0) {
              loadedProfiles[errorIndex] = current;
            }
          }
        }

        setProfiles(loadedProfiles);
        setCurrentProfile(current);

        // activeProfile is now simply the current profile
        // Multi-profile support: profiles array contains all profiles (owned + shared)
        // The user can switch between them using the profile switcher
        setActiveProfile(current);

        // Cache address/vehicle counts for sidebar badges (profile-level data is available now)
        if (current) {
          cacheItemCounts({
            address: current.addresses?.length ?? 0,
            personal: current.personalItems?.length ?? 0,
            vehicle: current.vehicles?.length ?? 0,
          });
        }

        // Data loading complete (including Google Drive for Pro users)
        setIsLoading(false);

        // Pre-fetch insurance, health, and financial data from separate collections
        // so pages load instantly when navigated to (matches Address/Vehicle pattern).
        // Runs in background — does not block profile loading.
        const isProUsingDrive = pilotData?.membershipPlan && usesGoogleDrive(pilotData.membershipPlan) && userChoseDrive;
        if (!isProUsingDrive && current?.id) {
          const pid = current.id;
          Promise.all([
            insuranceService.prefetch(pid),
            healthService.prefetch(pid),
            financialService.prefetch(pid),
          ]).then(() => {
            // Cache subcollection counts for sidebar badges after prefetch completes
            cacheItemCounts({
              insurance: insuranceService.list(pid).length,
              health: healthService.list(pid).length,
              financial: financialService.list(pid).length,
            });
          }).catch(error => {
            console.warn('Pre-fetch of entity data failed (non-critical):', error);
          });
        }

        // pilotInfo already fetched above, no need to fetch again
        if (current?.ownerId && !pilotData) {
          const fetchedPilotData = await ProfileService.getPilotInfo(current.ownerId);
          if (fetchedPilotData) {
            setPilotInfo(fetchedPilotData);
          }
        }

        // ── Co-Pilot: listen for removal or Pilot account deletion ────
        // Watches the families doc for:
        //   1. Doc deleted → Pilot deactivated their account
        //   2. Current user removed from memberUserIds → removed from family
        // Runs for ALL plans (including Pro where Firestore profile subscriptions are skipped).
        // Also handles dual-role users (Pilot + Co-Pilot) where the profile subscription
        // wouldn't trigger sign-out because the user's own Pilot profile still exists.
        if (userType === 'family' && current?.ownerId) {
          // Clean up any existing listener first
          if (unsubscribeFamilyDocRef.current) {
            unsubscribeFamilyDocRef.current();
            unsubscribeFamilyDocRef.current = null;
          }

          const familyDocRef = doc(db, 'families', current.ownerId);
          unsubscribeFamilyDocRef.current = onSnapshot(familyDocRef, (snap) => {
            if (!snap.exists()) {
              // Families doc deleted → Pilot deleted their account
              showToast.info('The family account has been deleted. You have been signed out.');
              firebaseAuthService.clearSessionData();
              navigate('/');
              return;
            }

            // Check if current user is still in the family
            // Skip during invitation acceptance — families doc may not be updated yet
            if (sessionStorage.getItem('pendingInvitation')) return;

            const familyData = snap.data();
            const memberUserIds: string[] = familyData?.memberUserIds || [];
            if (!memberUserIds.includes(userId)) {
              showToast.info('You have been removed from this family. Please sign in again.');
              firebaseAuthService.clearSessionData();
              navigate('/');
            }
          }, (error: any) => {
            // Permission denied = user was removed from memberUserIds → lost read access.
            // This is the PRIMARY detection path: Firestore security rules revoke access
            // before the snapshot callback can see the updated memberUserIds array.
            if (error?.code === 'permission-denied') {
              showToast.info('You have been removed from this family. Please sign in again.');
              firebaseAuthService.clearSessionData();
              navigate('/');
            } else {
              console.warn('Family doc listener error:', error);
            }
          });
        }

        // Set up real-time listener for profile updates
        // Skip Firestore subscriptions for Pro users who chose Google Drive
        const isProUserOnDrive = pilotData?.membershipPlan && usesGoogleDrive(pilotData.membershipPlan) && userChoseDrive;

        if (isProUserOnDrive) {
          return;
        }

        // CRITICAL: Unsubscribe from any existing subscription first to prevent multiple listeners
        if (unsubscribeProfilesRef.current) {
          unsubscribeProfilesRef.current();
          unsubscribeProfilesRef.current = null;
          // Small delay to ensure cleanup completes before creating new listener
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        if (userId && !unsubscribeProfilesRef.current) {
          unsubscribeProfilesRef.current = ProfileService.subscribeToProfiles(userId, (updatedProfiles) => {
            // CRITICAL FIX: Merge pending optimistic updates with Firestore updates
            // This prevents the listener from overwriting optimistic updates before they complete
            const now = Date.now();
            const mergedProfiles = updatedProfiles.map(profile => {
              const pendingKey = `${profile.id}`;
              const pending = pendingUpdatesRef.current.get(pendingKey);

              // For Pro users: ALWAYS preserve Google Drive data
              const driveData = googleDriveDataRef.current;
              if (driveData?.isProUser) {
                return {
                  ...profile,
                  addresses: driveData.addresses,
                  vehicles: driveData.vehicles,
                  insurancePolicies: driveData.insurancePolicies,
                };
              }

              if (pending) {
                // Check if Firestore data matches the pending update
                const firestoreMatchesPending = JSON.stringify(profile.addresses) === JSON.stringify(pending.updates.addresses);

                if (firestoreMatchesPending) {
                  // Firestore has synced! Use Firestore data and clear pending update
                  pendingUpdatesRef.current.delete(pendingKey);
                  return profile;
                } else if ((now - pending.timestamp) < 5000) {
                  // Firestore hasn't synced yet, and pending update is fresh - keep using pending data
                  return { ...profile, ...pending.updates };
                } else {
                  // Pending update is stale (>5 seconds) - clear it and use Firestore data
                  pendingUpdatesRef.current.delete(pendingKey);
                  return profile;
                }
              }

              return profile;
            });

            // Auto sign-out when profile disappears or plan changes.
            // Applies to ALL user types — Pilots (account deleted externally) and
            // Co-Pilots/Passengers (removed from family or Pilot deleted account).
            const forceSignOut = (message: string) => {
              showToast.info(message);
              firebaseAuthService.clearSessionData();
              navigate('/');
            };

            // Check 1: Profile disappeared — account deleted or member removed
            if (mergedProfiles.length === 0) {
              const currentUserType = localStorage.getItem('lucidDock_userType');
              forceSignOut(currentUserType === 'family'
                ? 'You have been removed from this family profile'
                : 'Your account has been deleted');
              return;
            }

            // Check 2 (family only): Plan changed — data source mismatch requires re-login
            const familyUserType = localStorage.getItem('lucidDock_userType');
            if (familyUserType === 'family') {
              const currentPlanCached = localStorage.getItem('lucidDock_cachedPlan');
              const familyCurrentProfileId = ProfileService.getCurrentProfileId();
              const familyProfile = mergedProfiles.find(p => p.id === familyCurrentProfileId);
              if (currentPlanCached && familyProfile?.membershipPlan && familyProfile.membershipPlan !== currentPlanCached) {
                forceSignOut('Your family plan has been updated. Please sign in again.');
                return;
              }
            }

            setProfiles(mergedProfiles);

            // Update current profile if it changed
            const currentProfileId = ProfileService.getCurrentProfileId();
            if (currentProfileId) {
              const updatedCurrent = mergedProfiles.find(p => p.id === currentProfileId);
              if (updatedCurrent) {
                setCurrentProfile(updatedCurrent);
                setActiveProfile(updatedCurrent);
              }
            }
          });
        }
      } catch (error) {
        console.error('Error initializing profiles:', error);
        setIsLoading(false);
        // Only redirect on permission errors if we have an authenticated user
        if (error instanceof Error && error.message.includes('permission')) {
          if (!authUser) {
            return;
          }
          // Clear stale session data and redirect
          firebaseAuthService.clearSessionData();
          navigate('/');
        }
      }
    }; // End of initializeAuth function

    // Call the async initialization function
    initializeAuth().finally(() => {
      initInProgressRef.current = false;
    });

    // Cleanup on unmount
    return () => {
      isMounted = false;
      initInProgressRef.current = false;
      // CRITICAL: Unsubscribe from profile listener to prevent memory leaks and Firestore errors
      if (unsubscribeProfilesRef.current) {
        unsubscribeProfilesRef.current();
        unsubscribeProfilesRef.current = null;
      }
      if (unsubscribeFamilyDocRef.current) {
        unsubscribeFamilyDocRef.current();
        unsubscribeFamilyDocRef.current = null;
      }
    };
  }, [navigate, location, authReady, authUser]);

  useEffect(() => {
    const handleDriveConnectionChanged = async () => {
      const profile = currentProfile;
      const plan = pilotInfo?.membershipPlan;
      const drivePreference = localStorage.getItem('lucidDock_storageProvider');
      if (!profile || !plan || !usesGoogleDrive(plan) || drivePreference === 'firebase') return;

      setIsDriveDataLoading(true);
      try {
        googleDriveRepository.reset();
        await googleDriveRepository.initialize(profile.id, profile.name);

        if (googleDriveRepository.isReady()) {
          const [driveAddresses, driveVehicles, driveInsurance] = await Promise.all([
            googleDriveRepository.getAddresses(profile.id),
            googleDriveRepository.getVehicles(profile.id),
            googleDriveRepository.getInsurancePolicies(profile.id),
          ]);

          googleDriveDataRef.current = {
            isProUser: true,
            addresses: driveAddresses,
            vehicles: driveVehicles,
            insurancePolicies: driveInsurance,
          };

          const updated = {
            ...profile,
            addresses: driveAddresses,
            vehicles: driveVehicles,
            insurancePolicies: driveInsurance,
          };
          setCurrentProfile(updated);
          setActiveProfile(updated);
          setProfiles(prev => prev.map(p => p.id === updated.id ? updated : p));

          syncCountsFromGoogleDriveMeta({
            addresses: driveAddresses.length,
            vehicles: driveVehicles.length,
            insurancePolicies: driveInsurance.length,
          });
        } else {
          console.warn('ProfileContext: Google Drive repository not ready after reconnect');
        }
      } catch (error) {
        console.error('ProfileContext: Error reloading Drive data after reconnect:', error);
      } finally {
        setIsDriveDataLoading(false);
      }
    };

    window.addEventListener('googleDriveConnectionChanged', handleDriveConnectionChanged);
    return () => {
      window.removeEventListener('googleDriveConnectionChanged', handleDriveConnectionChanged);
    };
  }, [currentProfile, pilotInfo]);

  const createProfile = async (name: string): Promise<Profile> => {
    const userId = ProfileService.getCurrentUserId();
    if (!userId) throw new Error('No user ID found');

    const newProfile = await ProfileService.createProfile(name, userId);
    // Real-time listener will update profiles automatically
    return newProfile;
  };

  const updateProfile = async (profileId: string, updates: Partial<Profile>): Promise<void> => {
    // Optimistic update - immediately update local state
    setProfiles(prevProfiles =>
      prevProfiles.map(p => p.id === profileId ? { ...p, ...updates } : p)
    );

    if (currentProfile?.id === profileId) {
      setCurrentProfile(prev => prev ? { ...prev, ...updates } : null);
    }

    // Persist to Firestore (real-time listener will sync any conflicts)
    await ProfileService.updateProfile(profileId, updates);
  };

  const deleteProfile = async (profileId: string): Promise<void> => {
    await ProfileService.deleteProfile(profileId);
    // Real-time listener will update profiles automatically

    // If we're deleting the current profile, switch to another one
    if (currentProfile?.id === profileId) {
      const remainingProfiles = profiles.filter(p => p.id !== profileId);
      if (remainingProfiles.length > 0) {
        ProfileService.setCurrentProfileId(remainingProfiles[0].id);
        setCurrentProfile(remainingProfiles[0]);
      } else {
        setCurrentProfile(null);
      }
    }
  };

  const switchProfile = (profileId: string): void => {
    const profile = profiles.find(p => p.id === profileId);
    if (profile) {
      ProfileService.setCurrentProfileId(profileId);
      // Store profile owner ID for encryption key derivation
      if (profile.ownerId) {
        ProfileService.setProfileOwnerId(profile.ownerId);
      }
      setCurrentProfile(profile);
    }
  };

  const addAddress = async (profileId: string, address: Omit<import('../types/profile').Address, 'id'>): Promise<string> => {
    // Generate a temporary ID for optimistic update
    const tempId = `temp_${Date.now()}`;
    const newAddress = { ...address, id: tempId, createdAt: address.createdAt || new Date().toISOString() } as import('../types/profile').Address;

    // Optimistically update local state immediately for instant UI update
    // If new address is primary, clear existing primary in same category
    setProfiles(prevProfiles =>
      prevProfiles.map(p => {
        if (p.id !== profileId) return p;

        let updatedAddresses = [...p.addresses];

        // If new address is being set as primary, clear existing primary in same category
        if (address.isDefault && address.type) {
          updatedAddresses = updatedAddresses.map(a => {
            if (a.isDefault && a.type === address.type) {
              return { ...a, isDefault: false };
            }
            return a;
          });
        }

        return { ...p, addresses: [...updatedAddresses, newAddress] };
      })
    );

    setCurrentProfile(prev => {
      if (!prev || prev.id !== profileId) return prev;

      let updatedAddresses = [...prev.addresses];

      // If new address is being set as primary, clear existing primary in same category
      if (address.isDefault && address.type) {
        updatedAddresses = updatedAddresses.map(a => {
          if (a.isDefault && a.type === address.type) {
            return { ...a, isDefault: false };
          }
          return a;
        });
      }

      return { ...prev, addresses: [...updatedAddresses, newAddress] };
    });

    // CRITICAL FIX: Register pending update to prevent Firestore listener from overwriting
    // the optimistic add (same pattern as updateAddress)
    // Use profilesRef (not closure `profiles`) to get the latest state
    const profile = profilesRef.current.find(p => p.id === profileId);
    if (profile) {
      let pendingAddresses = [...profile.addresses];
      if (address.isDefault && address.type) {
        pendingAddresses = pendingAddresses.map(a => {
          if (a.isDefault && a.type === address.type) {
            return { ...a, isDefault: false };
          }
          return a;
        });
      }
      pendingAddresses.push(newAddress);

      pendingUpdatesRef.current.set(profileId, {
        timestamp: Date.now(),
        updates: { addresses: pendingAddresses }
      });
    }

    try {
      // If setting as primary, first clear existing primary in same category
      if (address.isDefault && address.type) {
        const profile = profiles.find(p => p.id === profileId);
        const existingPrimary = profile?.addresses.find(
          a => a.isDefault && a.type === address.type
        );
        if (existingPrimary) {
          await storageRouterService.updateAddress(profileId, existingPrimary.id, { isDefault: false }, existingPrimary, currentProfile?.name);
        }
      }

      const actualId = await storageRouterService.addAddress(profileId, address, currentProfile?.name);

      // Replace temp ID with actual ID to avoid duplicates (critical for Pro users)
      const newAddressWithActualId = { ...address, id: actualId } as import('../types/profile').Address;
      setProfiles(prevProfiles =>
        prevProfiles.map(p => {
          if (p.id !== profileId) return p;
          return {
            ...p,
            addresses: p.addresses.map(a => a.id === tempId ? newAddressWithActualId : a),
          };
        })
      );
      setCurrentProfile(prev => {
        if (!prev || prev.id !== profileId) return prev;
        return {
          ...prev,
          addresses: prev.addresses.map(a => a.id === tempId ? newAddressWithActualId : a),
        };
      });

      if (googleDriveDataRef.current?.isProUser) {
        googleDriveDataRef.current = {
          ...googleDriveDataRef.current,
          addresses: [...googleDriveDataRef.current.addresses, newAddressWithActualId],
        };
      }

      // Update pending update ref with actual ID (so Firestore listener can match correctly)
      const pendingUpdate = pendingUpdatesRef.current.get(profileId);
      if (pendingUpdate?.updates.addresses) {
        pendingUpdatesRef.current.set(profileId, {
          timestamp: Date.now(),
          updates: {
            addresses: (pendingUpdate.updates.addresses as import('../types/profile').Address[]).map(
              (a: import('../types/profile').Address) => a.id === tempId ? newAddressWithActualId : a,
            ),
          },
        });
      }

      return actualId;
    } catch (error) {
      // Rollback optimistic update on error
      pendingUpdatesRef.current.delete(profileId);
      setProfiles(prevProfiles =>
        prevProfiles.map(p => {
          if (p.id !== profileId) return p;
          return { ...p, addresses: p.addresses.filter(a => a.id !== tempId) };
        })
      );
      setCurrentProfile(prev => {
        if (!prev || prev.id !== profileId) return prev;
        return { ...prev, addresses: prev.addresses.filter(a => a.id !== tempId) };
      });
      // Re-throw the error so it can be caught by the component
      throw error;
    }
  };

  const updateAddress = async (profileId: string, addressId: string, updates: Partial<import('../types/profile').Address>): Promise<void> => {
    // Optimistic update using functional updates to handle concurrent updates correctly
    setProfiles(prevProfiles => {
      const next = prevProfiles.map(p => {
        if (p.id !== profileId) return p;

        const addressIndex = p.addresses.findIndex(a => a.id === addressId);
        if (addressIndex === -1) return p;

        let updatedAddresses = [...p.addresses];

        // If setting this address as primary, clear other primaries in same category
        if (updates.isDefault === true) {
          const targetAddress = updatedAddresses[addressIndex];
          const addressType = updates.type || targetAddress.type;
          updatedAddresses = updatedAddresses.map((a, idx) => {
            if (idx !== addressIndex && a.isDefault && a.type === addressType) {
              return { ...a, isDefault: false };
            }
            return a;
          });
        }

        updatedAddresses[addressIndex] = {
          ...updatedAddresses[addressIndex],
          ...updates,
          id: addressId
        };

        return { ...p, addresses: updatedAddresses };
      });
      // Update ref + pending updates immediately so concurrent async callbacks see fresh data
      profilesRef.current = next;
      const updatedProfile = next.find(p => p.id === profileId);
      if (updatedProfile) {
        pendingUpdatesRef.current.set(profileId, {
          timestamp: Date.now(),
          updates: { addresses: updatedProfile.addresses }
        });
      }
      return next;
    });

    // Also update currentProfile using functional update
    setCurrentProfile(prev => {
      if (!prev || prev.id !== profileId) return prev;

      const addressIndex = prev.addresses.findIndex(a => a.id === addressId);
      if (addressIndex === -1) return prev;

      let updatedAddresses = [...prev.addresses];

      // If setting this address as primary, clear other primaries in same category
      if (updates.isDefault === true) {
        const targetAddress = updatedAddresses[addressIndex];
        const addressType = updates.type || targetAddress.type;
        updatedAddresses = updatedAddresses.map((a, idx) => {
          if (idx !== addressIndex && a.isDefault && a.type === addressType) {
            return { ...a, isDefault: false };
          }
          return a;
        });
      }

      updatedAddresses[addressIndex] = {
        ...updatedAddresses[addressIndex],
        ...updates,
        id: addressId
      };

      return { ...prev, addresses: updatedAddresses };
    });

    // Persist to storage (Firestore or Google Drive based on user's plan)
    // If setting as primary, first clear existing primary in same category
    if (updates.isDefault === true) {
      const profile = profiles.find(p => p.id === profileId);
      const targetAddress = profile?.addresses.find(a => a.id === addressId);
      const addressType = updates.type || targetAddress?.type;
      if (addressType) {
        const existingPrimary = profile?.addresses.find(
          a => a.id !== addressId && a.isDefault && a.type === addressType
        );
        if (existingPrimary) {
          await storageRouterService.updateAddress(profileId, existingPrimary.id, { isDefault: false }, existingPrimary, currentProfile?.name);
        }
      }
    }

    const profileForUpdate = profilesRef.current.find(p => p.id === profileId);
    const targetAddress = profileForUpdate?.addresses.find(a => a.id === addressId);
    if (targetAddress) {
      await storageRouterService.updateAddress(profileId, addressId, updates, targetAddress, currentProfile?.name);

      if (googleDriveDataRef.current?.isProUser) {
        googleDriveDataRef.current = {
          ...googleDriveDataRef.current,
          addresses: googleDriveDataRef.current.addresses.map(a =>
            a.id === addressId ? { ...a, ...updates } : a
          ),
        };
      }
    }

    // CRITICAL FIX: Don't clear pending update immediately!
    // The Firestore listener may not have received the update yet.
    // Let the listener clear it when it receives matching data or after 5 seconds (handled in listener)
  };

  const deleteAddress = async (profileId: string, addressId: string): Promise<void> => {
    // Optimistic update - remove from local state immediately
    setProfiles(prevProfiles => {
      return prevProfiles.map(p => {
        if (p.id !== profileId) return p;
        return { ...p, addresses: p.addresses.filter(a => a.id !== addressId) };
      });
    });

    setCurrentProfile(prev => {
      if (!prev || prev.id !== profileId) return prev;
      return { ...prev, addresses: prev.addresses.filter(a => a.id !== addressId) };
    });

    await storageRouterService.deleteAddress(profileId, addressId, currentProfile?.name);

    if (googleDriveDataRef.current?.isProUser) {
      googleDriveDataRef.current = {
        ...googleDriveDataRef.current,
        addresses: googleDriveDataRef.current.addresses.filter(a => a.id !== addressId),
      };
    }
  };

  const toggleFavoriteAddress = async (profileId: string, addressId: string): Promise<void> => {
    // Use functional updates for optimistic UI
    let newFavoriteValue = false;

    setProfiles(prevProfiles => {
      return prevProfiles.map(p => {
        if (p.id !== profileId) return p;

        const addressIndex = p.addresses.findIndex(a => a.id === addressId);
        if (addressIndex === -1) return p;

        const address = p.addresses[addressIndex];
        newFavoriteValue = !address.isFavorite;

        const updatedAddresses = [...p.addresses];
        updatedAddresses[addressIndex] = {
          ...updatedAddresses[addressIndex],
          isFavorite: newFavoriteValue
        };

        return { ...p, addresses: updatedAddresses };
      });
    });

    setCurrentProfile(prev => {
      if (!prev || prev.id !== profileId) return prev;

      const addressIndex = prev.addresses.findIndex(a => a.id === addressId);
      if (addressIndex === -1) return prev;

      const updatedAddresses = [...prev.addresses];
      updatedAddresses[addressIndex] = {
        ...updatedAddresses[addressIndex],
        isFavorite: newFavoriteValue
      };

      return { ...prev, addresses: updatedAddresses };
    });

    const profile = profiles.find(p => p.id === profileId);
    const targetAddress = profile?.addresses.find(a => a.id === addressId);
    if (targetAddress) {
      await storageRouterService.updateAddress(profileId, addressId, { isFavorite: newFavoriteValue }, targetAddress, currentProfile?.name);
    }
  };

  // ── Personal Item CRUD ─────────────────────────────────────
  const addPersonalItem = async (profileId: string, item: Omit<import('../types/profile').PersonalItem, 'id'>): Promise<string> => {
    const tempId = `temp_${Date.now()}`;
    const newItem = { ...item, id: tempId } as import('../types/profile').PersonalItem;

    setProfiles(prev => prev.map(p => p.id !== profileId ? p : { ...p, personalItems: [...(p.personalItems || []), newItem] }));
    setCurrentProfile(prev => !prev || prev.id !== profileId ? prev : { ...prev, personalItems: [...(prev.personalItems || []), newItem] });

    const profile = profilesRef.current.find(p => p.id === profileId);
    if (profile) {
      pendingUpdatesRef.current.set(profileId, {
        timestamp: Date.now(),
        updates: { personalItems: [...(profile.personalItems || []), newItem] },
      });
    }

    try {
      const actualId = await ProfileService.addPersonalItem(profileId, item);
      const withActualId = { ...item, id: actualId } as import('../types/profile').PersonalItem;

      setProfiles(prev => prev.map(p => p.id !== profileId ? p : { ...p, personalItems: (p.personalItems || []).map(i => i.id === tempId ? withActualId : i) }));
      setCurrentProfile(prev => !prev || prev.id !== profileId ? prev : { ...prev, personalItems: (prev.personalItems || []).map(i => i.id === tempId ? withActualId : i) });

      const pending = pendingUpdatesRef.current.get(profileId);
      if (pending?.updates.personalItems) {
        pendingUpdatesRef.current.set(profileId, {
          timestamp: Date.now(),
          updates: { personalItems: (pending.updates.personalItems as import('../types/profile').PersonalItem[]).map(i => i.id === tempId ? withActualId : i) },
        });
      }

      return actualId;
    } catch (error) {
      pendingUpdatesRef.current.delete(profileId);
      setProfiles(prev => prev.map(p => p.id !== profileId ? p : { ...p, personalItems: (p.personalItems || []).filter(i => i.id !== tempId) }));
      setCurrentProfile(prev => !prev || prev.id !== profileId ? prev : { ...prev, personalItems: (prev.personalItems || []).filter(i => i.id !== tempId) });
      throw error;
    }
  };

  const updatePersonalItem = async (profileId: string, itemId: string, updates: Partial<import('../types/profile').PersonalItem>): Promise<void> => {
    setProfiles(prev => prev.map(p => {
      if (p.id !== profileId) return p;
      return { ...p, personalItems: (p.personalItems || []).map(i => i.id === itemId ? { ...i, ...updates } : i) };
    }));
    setCurrentProfile(prev => {
      if (!prev || prev.id !== profileId) return prev;
      return { ...prev, personalItems: (prev.personalItems || []).map(i => i.id === itemId ? { ...i, ...updates } : i) };
    });

    const profile = profilesRef.current.find(p => p.id === profileId);
    if (profile) {
      pendingUpdatesRef.current.set(profileId, {
        timestamp: Date.now(),
        updates: { personalItems: (profile.personalItems || []).map(i => i.id === itemId ? { ...i, ...updates } : i) },
      });
    }

    await ProfileService.updatePersonalItem(profileId, itemId, updates);
  };

  const deletePersonalItem = async (profileId: string, itemId: string): Promise<void> => {
    setProfiles(prev => prev.map(p => p.id !== profileId ? p : { ...p, personalItems: (p.personalItems || []).filter(i => i.id !== itemId) }));
    setCurrentProfile(prev => !prev || prev.id !== profileId ? prev : { ...prev, personalItems: (prev.personalItems || []).filter(i => i.id !== itemId) });
    await ProfileService.deletePersonalItem(profileId, itemId);
  };

  const addVehicle = async (profileId: string, vehicle: Omit<import('../types/profile').Vehicle, 'id'>): Promise<void> => {
    // Generate a temporary ID for optimistic update
    const tempId = `temp_${Date.now()}`;
    const newVehicle = { ...vehicle, id: tempId } as import('../types/profile').Vehicle;

    // Optimistically update local state immediately for instant UI update
    // If new vehicle is primary, clear existing primary in same category
    setProfiles(prevProfiles =>
      prevProfiles.map(p => {
        if (p.id !== profileId) return p;

        let updatedVehicles = [...p.vehicles];

        // If new vehicle is being set as primary, clear existing primary in same category
        if (vehicle.isDefault && vehicle.type) {
          updatedVehicles = updatedVehicles.map(v => {
            if (v.isDefault && v.type === vehicle.type) {
              return { ...v, isDefault: false };
            }
            return v;
          });
        }

        return { ...p, vehicles: [...updatedVehicles, newVehicle] };
      })
    );

    setCurrentProfile(prev => {
      if (!prev || prev.id !== profileId) return prev;

      let updatedVehicles = [...prev.vehicles];

      // If new vehicle is being set as primary, clear existing primary in same category
      if (vehicle.isDefault && vehicle.type) {
        updatedVehicles = updatedVehicles.map(v => {
          if (v.isDefault && v.type === vehicle.type) {
            return { ...v, isDefault: false };
          }
          return v;
        });
      }

      return { ...prev, vehicles: [...updatedVehicles, newVehicle] };
    });

    try {
      // If setting as primary, first clear existing primary in same category
      if (vehicle.isDefault && vehicle.type) {
        const profile = profiles.find(p => p.id === profileId);
        const existingPrimary = profile?.vehicles.find(
          v => v.isDefault && v.type === vehicle.type
        );
        if (existingPrimary) {
          await storageRouterService.updateVehicle(profileId, existingPrimary.id, { isDefault: false }, existingPrimary, currentProfile?.name);
        }
      }

      await storageRouterService.addVehicle(profileId, vehicle, currentProfile?.name);
    } catch (error) {
      // Rollback optimistic update on error
      setProfiles(prevProfiles =>
        prevProfiles.map(p => {
          if (p.id !== profileId) return p;
          return { ...p, vehicles: p.vehicles.filter(v => v.id !== tempId) };
        })
      );
      setCurrentProfile(prev => {
        if (!prev || prev.id !== profileId) return prev;
        return { ...prev, vehicles: prev.vehicles.filter(v => v.id !== tempId) };
      });
      throw error;
    }
  };

  const updateVehicle = async (profileId: string, vehicleId: string, updates: Partial<import('../types/profile').Vehicle>): Promise<void> => {
    // Optimistic update using functional updates to handle concurrent updates correctly
    setProfiles(prevProfiles => {
      const next = prevProfiles.map(p => {
        if (p.id !== profileId) return p;

        const vehicleIndex = p.vehicles.findIndex(v => v.id === vehicleId);
        if (vehicleIndex === -1) return p;

        let updatedVehicles = [...p.vehicles];

        // If setting this vehicle as primary, clear other primaries in same category
        if (updates.isDefault === true) {
          const targetVehicle = updatedVehicles[vehicleIndex];
          const vehicleType = updates.type || targetVehicle.type;
          updatedVehicles = updatedVehicles.map((v, idx) => {
            if (idx !== vehicleIndex && v.isDefault && v.type === vehicleType) {
              return { ...v, isDefault: false };
            }
            return v;
          });
        }

        updatedVehicles[vehicleIndex] = {
          ...updatedVehicles[vehicleIndex],
          ...updates,
          id: vehicleId
        };

        return { ...p, vehicles: updatedVehicles };
      });
      profilesRef.current = next;
      return next;
    });

    // Also update currentProfile using functional update
    setCurrentProfile(prev => {
      if (!prev || prev.id !== profileId) return prev;

      const vehicleIndex = prev.vehicles.findIndex(v => v.id === vehicleId);
      if (vehicleIndex === -1) return prev;

      let updatedVehicles = [...prev.vehicles];

      // If setting this vehicle as primary, clear other primaries in same category
      if (updates.isDefault === true) {
        const targetVehicle = updatedVehicles[vehicleIndex];
        const vehicleType = updates.type || targetVehicle.type;
        updatedVehicles = updatedVehicles.map((v, idx) => {
          if (idx !== vehicleIndex && v.isDefault && v.type === vehicleType) {
            return { ...v, isDefault: false };
          }
          return v;
        });
      }

      updatedVehicles[vehicleIndex] = {
        ...updatedVehicles[vehicleIndex],
        ...updates,
        id: vehicleId
      };

      return { ...prev, vehicles: updatedVehicles };
    });

    if (updates.isDefault === true) {
      const profile = profiles.find(p => p.id === profileId);
      const targetVehicle = profile?.vehicles.find(v => v.id === vehicleId);
      const vehicleType = updates.type || targetVehicle?.type;
      if (vehicleType) {
        const existingPrimary = profile?.vehicles.find(
          v => v.id !== vehicleId && v.isDefault && v.type === vehicleType
        );
        if (existingPrimary) {
          await storageRouterService.updateVehicle(profileId, existingPrimary.id, { isDefault: false }, existingPrimary, currentProfile?.name);
        }
      }
    }

    const profile = profilesRef.current.find(p => p.id === profileId);
    const targetVehicle = profile?.vehicles.find(v => v.id === vehicleId);
    if (targetVehicle) {
      await storageRouterService.updateVehicle(profileId, vehicleId, updates, targetVehicle, currentProfile?.name);
    }
  };

  const deleteVehicle = async (profileId: string, vehicleId: string): Promise<void> => {
    // Optimistic update - immediately remove from local state for instant UI feedback
    setProfiles(prevProfiles =>
      prevProfiles.map(p =>
        p.id === profileId
          ? { ...p, vehicles: p.vehicles.filter(v => v.id !== vehicleId) }
          : p
      )
    );

    if (currentProfile?.id === profileId) {
      setCurrentProfile(prev =>
        prev ? { ...prev, vehicles: prev.vehicles.filter(v => v.id !== vehicleId) } : null
      );
    }

    await storageRouterService.deleteVehicle(profileId, vehicleId, currentProfile?.name);
  };

  const addInsurancePolicy = async (profileId: string, policy: Omit<import('../types/profile').InsurancePolicy, 'id'>): Promise<void> => {
    // Generate a temporary ID for optimistic update
    const tempId = `temp_${Date.now()}`;
    const newPolicy = { ...policy, id: tempId } as import('../types/profile').InsurancePolicy;

    // Optimistic update - immediately add to local state
    // If new policy is primary, clear existing primary in same insurance type category
    setProfiles(prevProfiles =>
      prevProfiles.map(p => {
        if (p.id !== profileId) return p;

        let updatedPolicies = [...p.insurancePolicies];

        // If new policy is being set as primary, clear existing primary in same insuranceType
        if (policy.isPrimary && policy.insuranceType) {
          updatedPolicies = updatedPolicies.map(ip => {
            if (ip.isPrimary && ip.insuranceType === policy.insuranceType) {
              return { ...ip, isPrimary: false };
            }
            return ip;
          });
        }

        return { ...p, insurancePolicies: [...updatedPolicies, newPolicy] };
      })
    );

    setCurrentProfile(prev => {
      if (!prev || prev.id !== profileId) return prev;

      let updatedPolicies = [...prev.insurancePolicies];

      // If new policy is being set as primary, clear existing primary in same insuranceType
      if (policy.isPrimary && policy.insuranceType) {
        updatedPolicies = updatedPolicies.map(ip => {
          if (ip.isPrimary && ip.insuranceType === policy.insuranceType) {
            return { ...ip, isPrimary: false };
          }
          return ip;
        });
      }

      return { ...prev, insurancePolicies: [...updatedPolicies, newPolicy] };
    });

    try {
      // If setting as primary, first clear existing primary in same insuranceType category
      if (policy.isPrimary && policy.insuranceType) {
        const profile = profiles.find(p => p.id === profileId);
        const existingPrimary = profile?.insurancePolicies.find(
          ip => ip.isPrimary && ip.insuranceType === policy.insuranceType
        );
        if (existingPrimary) {
          await storageRouterService.updateInsurancePolicy(profileId, existingPrimary.id, { isPrimary: false }, existingPrimary, currentProfile?.name);
        }
      }

      await storageRouterService.addInsurancePolicy(profileId, policy, currentProfile?.name);
    } catch (error) {
      // Rollback on error
      setProfiles(prevProfiles =>
        prevProfiles.map(p =>
          p.id === profileId
            ? { ...p, insurancePolicies: p.insurancePolicies.filter(ip => ip.id !== tempId) }
            : p
        )
      );
      if (currentProfile?.id === profileId) {
        setCurrentProfile(prev =>
          prev ? { ...prev, insurancePolicies: prev.insurancePolicies.filter(ip => ip.id !== tempId) } : null
        );
      }
      throw error;
    }
  };

  const updateInsurancePolicy = async (profileId: string, policyId: string, updates: Partial<import('../types/profile').InsurancePolicy>): Promise<void> => {
    // Optimistic update using functional updates to handle concurrent updates correctly
    setProfiles(prevProfiles => {
      return prevProfiles.map(p => {
        if (p.id !== profileId) return p;

        const policyIndex = p.insurancePolicies.findIndex(ip => ip.id === policyId);
        if (policyIndex === -1) return p;

        let updatedPolicies = [...p.insurancePolicies];

        // If setting this policy as primary, clear other primaries in same insuranceType category
        if (updates.isPrimary === true) {
          const targetPolicy = updatedPolicies[policyIndex];
          const insuranceType = updates.insuranceType || targetPolicy.insuranceType;
          updatedPolicies = updatedPolicies.map((ip, idx) => {
            if (idx !== policyIndex && ip.isPrimary && ip.insuranceType === insuranceType) {
              return { ...ip, isPrimary: false };
            }
            return ip;
          });
        }

        updatedPolicies[policyIndex] = {
          ...updatedPolicies[policyIndex],
          ...updates,
          id: policyId
        };

        return { ...p, insurancePolicies: updatedPolicies };
      });
    });

    // Also update currentProfile using functional update
    setCurrentProfile(prev => {
      if (!prev || prev.id !== profileId) return prev;

      const policyIndex = prev.insurancePolicies.findIndex(ip => ip.id === policyId);
      if (policyIndex === -1) return prev;

      let updatedPolicies = [...prev.insurancePolicies];

      // If setting this policy as primary, clear other primaries in same insuranceType category
      if (updates.isPrimary === true) {
        const targetPolicy = updatedPolicies[policyIndex];
        const insuranceType = updates.insuranceType || targetPolicy.insuranceType;
        updatedPolicies = updatedPolicies.map((ip, idx) => {
          if (idx !== policyIndex && ip.isPrimary && ip.insuranceType === insuranceType) {
            return { ...ip, isPrimary: false };
          }
          return ip;
        });
      }

      updatedPolicies[policyIndex] = {
        ...updatedPolicies[policyIndex],
        ...updates,
        id: policyId
      };

      return { ...prev, insurancePolicies: updatedPolicies };
    });

    if (updates.isPrimary === true) {
      const profile = profiles.find(p => p.id === profileId);
      const targetPolicy = profile?.insurancePolicies.find(ip => ip.id === policyId);
      const insuranceType = updates.insuranceType || targetPolicy?.insuranceType;
      if (insuranceType) {
        const existingPrimary = profile?.insurancePolicies.find(
          ip => ip.id !== policyId && ip.isPrimary && ip.insuranceType === insuranceType
        );
        if (existingPrimary) {
          await storageRouterService.updateInsurancePolicy(profileId, existingPrimary.id, { isPrimary: false }, existingPrimary, currentProfile?.name);
        }
      }
    }

    const profile = profiles.find(p => p.id === profileId);
    const targetPolicy = profile?.insurancePolicies.find(ip => ip.id === policyId);
    if (targetPolicy) {
      await storageRouterService.updateInsurancePolicy(profileId, policyId, updates, targetPolicy, currentProfile?.name);
    }
  };

  const deleteInsurancePolicy = async (profileId: string, policyId: string): Promise<void> => {
    // Optimistic update - immediately remove from local state
    setProfiles(prevProfiles =>
      prevProfiles.map(p =>
        p.id === profileId
          ? { ...p, insurancePolicies: p.insurancePolicies.filter(ip => ip.id !== policyId) }
          : p
      )
    );

    if (currentProfile?.id === profileId) {
      setCurrentProfile(prev =>
        prev ? { ...prev, insurancePolicies: prev.insurancePolicies.filter(ip => ip.id !== policyId) } : null
      );
    }

    await storageRouterService.deleteInsurancePolicy(profileId, policyId, currentProfile?.name);
  };

  const addEmergencyContact = async (profileId: string, contact: Omit<import('../types/profile').EmergencyContact, 'id'>): Promise<void> => {
    // Generate a temporary ID for optimistic update
    const tempId = `temp_${Date.now()}`;
    const newContact = { ...contact, id: tempId } as import('../types/profile').EmergencyContact;

    // Optimistic update - immediately add to local state
    setProfiles(prevProfiles =>
      prevProfiles.map(p =>
        p.id === profileId
          ? { ...p, emergencyContacts: [...(p.emergencyContacts || []), newContact] }
          : p
      )
    );

    if (currentProfile?.id === profileId) {
      setCurrentProfile(prev =>
        prev ? { ...prev, emergencyContacts: [...(prev.emergencyContacts || []), newContact] } : null
      );
    }

    try {
      await ProfileService.addEmergencyContact(profileId, contact);
    } catch (error) {
      // Rollback on error
      setProfiles(prevProfiles =>
        prevProfiles.map(p =>
          p.id === profileId
            ? { ...p, emergencyContacts: (p.emergencyContacts || []).filter(ec => ec.id !== tempId) }
            : p
        )
      );
      if (currentProfile?.id === profileId) {
        setCurrentProfile(prev =>
          prev ? { ...prev, emergencyContacts: (prev.emergencyContacts || []).filter(ec => ec.id !== tempId) } : null
        );
      }
      throw error;
    }
  };

  const updateEmergencyContact = async (profileId: string, contactId: string, updates: Partial<import('../types/profile').EmergencyContact>): Promise<void> => {
    // Optimistic update - immediately update local state
    setProfiles(prevProfiles =>
      prevProfiles.map(p =>
        p.id === profileId
          ? {
              ...p,
              emergencyContacts: (p.emergencyContacts || []).map(ec =>
                ec.id === contactId ? { ...ec, ...updates } : ec
              )
            }
          : p
      )
    );

    if (currentProfile?.id === profileId) {
      setCurrentProfile(prev =>
        prev
          ? {
              ...prev,
              emergencyContacts: (prev.emergencyContacts || []).map(ec =>
                ec.id === contactId ? { ...ec, ...updates } : ec
              )
            }
          : null
      );
    }

    await ProfileService.updateEmergencyContact(profileId, contactId, updates);
  };

  const deleteEmergencyContact = async (profileId: string, contactId: string): Promise<void> => {
    // Optimistic update - immediately remove from local state
    setProfiles(prevProfiles =>
      prevProfiles.map(p =>
        p.id === profileId
          ? { ...p, emergencyContacts: (p.emergencyContacts || []).filter(ec => ec.id !== contactId) }
          : p
      )
    );

    if (currentProfile?.id === profileId) {
      setCurrentProfile(prev =>
        prev ? { ...prev, emergencyContacts: (prev.emergencyContacts || []).filter(ec => ec.id !== contactId) } : null
      );
    }

    await ProfileService.deleteEmergencyContact(profileId, contactId);
  };

  /** Re-fetch pilotInfo from Firestore (e.g. after a plan change via Cloud Function). */
  const refreshPilotInfo = async (): Promise<void> => {
    const userId = ProfileService.getCurrentUserId();
    if (!userId) return;
    const freshPilot = await ProfileService.getPilotInfo(userId);
    if (freshPilot) {
      setPilotInfo(freshPilot);
      if (freshPilot.membershipPlan) {
        localStorage.setItem('lucidDock_membershipPlan', freshPilot.membershipPlan);
        localStorage.setItem('lucidDock_cachedPlan', freshPilot.membershipPlan);
      }
    }
  };

  // REMOVED [Business Mode]: always false — app is personal/family only
  const isBusinessUser = false;

  const switchAccountType = async (type: 'personal' | 'business'): Promise<void> => {
    localStorage.setItem('lucidDock_accountType', type);
    const ownerId = currentProfile?.ownerId;
    // Find the matching profile in memory (same owner, different type)
    const target = profiles.find(p => p.ownerId === ownerId && p.type === type);
    if (target) {
      ProfileService.setCurrentProfileId(target.id);
      if (target.ownerId) ProfileService.setProfileOwnerId(target.ownerId);
      setCurrentProfile(target);
    } else if (ownerId) {
      // Create the profile of the new type on-demand
      const newProfile = await ProfileService.createProfile(currentProfile?.name || '', ownerId, type);
      setProfiles(prev => [...prev, newProfile]);
      ProfileService.setCurrentProfileId(newProfile.id);
      ProfileService.setProfileOwnerId(ownerId);
      setCurrentProfile(newProfile);
    }
    // Persist to Firestore
    if (ownerId) {
      ProfileService.updatePilotInfo(ownerId, { accountType: type }).catch(err => console.warn('Profile sync failed:', err));
    }
  };

  const contextValue: ProfileContextType = {
    currentProfile,
    activeProfile,
    profiles,
    pilotInfo,
    isLoading,
    isDriveDataLoading,
    isBusinessUser,
    switchAccountType,
    createProfile,
    updateProfile,
    deleteProfile,
    switchProfile,
    addAddress,
    updateAddress,
    deleteAddress,
    toggleFavoriteAddress,
    addPersonalItem,
    updatePersonalItem,
    deletePersonalItem,
    addVehicle,
    updateVehicle,
    deleteVehicle,
    addInsurancePolicy,
    updateInsurancePolicy,
    deleteInsurancePolicy,
    addEmergencyContact,
    updateEmergencyContact,
    deleteEmergencyContact,
    refreshPilotInfo,
  };

  return (
    <ProfileContext.Provider value={contextValue}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile(): ProfileContextType {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
}
